import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { ScheduleKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { LocationChips } from '@frontend/location-chips';
import { Button, Heading, Modal, ModalControlResponse, Text, useTooltip } from '@frontend/design-system';
import { AIRScheduleKnowledgeProvider, useAIRScheduleKnowledgeStore } from '../../../../stores/air-settings.store';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { SchedulingInstructionAccordionForm } from './scheduling-instruction-accordion-form';
import { SCHEDULING_MODES, SchedulingMode } from './types';

type Props = {
  modalControls: ModalControlResponse;
  filteredLocationIds: string[];
};

export const ScheduleInstructionBulkEditModal = ({ modalControls, filteredLocationIds }: Props) => {
  const { draftSettings, updateBulkDraft } = useAIRScheduleKnowledgeStore(['draftSettings', 'updateBulkDraft']);

  const initialState = useMemo(() => {
    if (!modalControls.modalProps.show) {
      return { schedulingType: SCHEDULING_MODES.DO_NOT_HELP };
    }
    if (filteredLocationIds.length === 0) {
      return { schedulingType: SCHEDULING_MODES.DO_NOT_HELP };
    }
    const firstMode = draftSettings[filteredLocationIds[0]]?.schedulingType;
    const allSame = filteredLocationIds.every((id) => draftSettings[id]?.schedulingType === firstMode);
    return { schedulingType: allSame ? firstMode : SCHEDULING_MODES.DO_NOT_HELP };
  }, [modalControls.modalProps.show, filteredLocationIds, draftSettings]);

  const handleConfirm = useCallback(
    (data: ScheduleKnowledge) => {
      updateBulkDraft(filteredLocationIds, data);
    },
    [filteredLocationIds, updateBulkDraft]
  );

  return (
    <AIRScheduleKnowledgeProvider
      key={modalControls.modalProps.show ? 'open' : 'closed'}
      initialState={{ draftSettings: { bulk: initialState } }}
    >
      <ScheduleInstructionBulkEditModalContent
        modalControls={modalControls}
        filteredLocationIds={filteredLocationIds}
        onConfirm={handleConfirm}
      />
    </AIRScheduleKnowledgeProvider>
  );
};

const ScheduleInstructionBulkEditModalContent = ({
  modalControls,
  filteredLocationIds,
  onConfirm,
}: Props & {
  onConfirm: (data: ScheduleKnowledge) => void;
}) => {
  const { t } = useTranslation('ai-receptionist-settings');
  const { draftSettings, updateDraft, clearDraft } = useAIRScheduleKnowledgeStore([
    'draftSettings',
    'updateDraft',
    'clearDraft',
  ]);

  const {
    triggerProps: doneTriggerProps,
    tooltipProps: doneTooltipProps,
    Tooltip: DoneTooltip,
  } = useTooltip({
    placement: 'top',
  });

  const handleDone = () => {
    const localData = draftSettings['bulk'] || ({} as ScheduleKnowledge);
    onConfirm(localData);
    modalControls.closeModal();
  };

  const onClose = () => {
    clearDraft();
    modalControls.closeModal();
  };

  const mode = (draftSettings['bulk']?.schedulingType as SchedulingMode) || SCHEDULING_MODES.DO_NOT_HELP;

  const handleModeChange = (newMode: SchedulingMode) => {
    updateDraft('bulk', { schedulingType: newMode });
  };

  return (
    <Modal {...modalControls.modalProps} minWidth={600}>
      <Modal.Header onClose={onClose}>
        <div
          css={css`
            display: flex;
            gap: var(--fabric-space-gap-sm);
          `}
        >
          <Heading level={2}>{t('Manage All')}</Heading>
          <LocationChips filteredLocationIds={filteredLocationIds} />
        </div>
      </Modal.Header>

      <Modal.Body>
        <SchedulingInstructionAccordionForm mode={mode} onChange={handleModeChange} />
      </Modal.Body>

      <Modal.Footer>
        <div css={styles.actionBar}>
          <Text
            size='medium'
            color='subdued'
            css={css`
              display: flex;
              align-items: center;
              gap: var(--fabric-space-gap-xxs);
            `}
          >
            {t("Click 'Done' to confirm changes only.")}
            <span
              {...doneTriggerProps}
              css={css`
                cursor: pointer;
                display: inline-flex;
              `}
            >
              <Icon color='subdued' name='info-small' />
            </span>
          </Text>
          <Button
            variant='secondary'
            onClick={onClose}
            trackingId={AiReceptionistTrackingIds.schedulingInstructionCancelButton}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={handleDone} trackingId={AiReceptionistTrackingIds.schedulingInstructionMultiDoneButton}>
            {t('Done')}
          </Button>
        </div>
        <DoneTooltip {...doneTooltipProps} style={{ ...doneTooltipProps.style, maxWidth: '280px' }}>
          {t("AI Receptionist's will only update when you click 'Save' after Manage All is closed.")}
        </DoneTooltip>
      </Modal.Footer>
    </Modal>
  );
};

const styles = {
  actionBar: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--fabric-space-gap-sm);
  `,
};
